/**
 * The browser half of the two WebAuthn ceremonies.
 *
 * ── Why the manual base64url ───────────────────────────────────────────────
 *
 * `navigator.credentials` speaks ArrayBuffer; the server speaks base64url JSON.
 * Newer browsers expose `PublicKeyCredential.parseCreationOptionsFromJSON`,
 * but not all of them do, and the conversion is a dozen lines. ⚠ It is UNPADDED
 * base64url in both directions — `atob` needs the padding restored, and sending
 * padded values back produces a challenge mismatch with no other symptom.
 */

function b64urlToBuf(s: string): ArrayBuffer {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  const bin = atob(padded);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

function bufToB64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i] ?? 0);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function isSupported(): boolean {
  return (
    typeof window !== 'undefined' && !!window.PublicKeyCredential && !!navigator.credentials?.create
  );
}

interface CreationOptionsJSON {
  challenge: string;
  rp: { id: string; name: string };
  user: { id: string; name: string; displayName: string };
  pubKeyCredParams: Array<{ type: 'public-key'; alg: number }>;
  timeout: number;
  attestation: AttestationConveyancePreference;
  authenticatorSelection: AuthenticatorSelectionCriteria;
  excludeCredentials?: Array<{ type: 'public-key'; id: string }>;
}

interface RequestOptionsJSON {
  challenge: string;
  rpId: string;
  timeout: number;
  userVerification: UserVerificationRequirement;
}

export async function createCredential(options: CreationOptionsJSON) {
  const publicKey: PublicKeyCredentialCreationOptions = {
    ...options,
    challenge: b64urlToBuf(options.challenge),
    user: { ...options.user, id: b64urlToBuf(options.user.id) },
    excludeCredentials: (options.excludeCredentials ?? []).map((c) => ({
      type: 'public-key' as const,
      id: b64urlToBuf(c.id),
    })),
  };

  const cred = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null;
  if (!cred) throw new Error('No passkey was created.');
  const response = cred.response as AuthenticatorAttestationResponse;

  return {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufToB64url(response.clientDataJSON),
      attestationObject: bufToB64url(response.attestationObject),
      transports: response.getTransports?.() ?? [],
    },
  };
}

export async function getAssertion(options: RequestOptionsJSON) {
  const publicKey: PublicKeyCredentialRequestOptions = {
    ...options,
    challenge: b64urlToBuf(options.challenge),
  };

  const cred = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null;
  if (!cred) throw new Error('No passkey was offered.');
  const response = cred.response as AuthenticatorAssertionResponse;

  return {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufToB64url(response.clientDataJSON),
      authenticatorData: bufToB64url(response.authenticatorData),
      signature: bufToB64url(response.signature),
      userHandle: response.userHandle ? bufToB64url(response.userHandle) : undefined,
    },
  };
}

/**
 * Turn a WebAuthn DOMException into something a human can act on.
 *
 * ⚠ The browser's own messages are deliberately vague — they must not tell an
 * attacker whether a credential exists — so "NotAllowedError" covers "you
 * cancelled", "it timed out" and "no key matched" alike. Saying that plainly
 * beats showing the raw name.
 */
export function explain(e: unknown): string {
  if (e instanceof DOMException) {
    if (e.name === 'NotAllowedError') {
      return 'No passkey was used. You may have cancelled, or it timed out — try again.';
    }
    if (e.name === 'InvalidStateError') {
      return 'This device already has a passkey for this account.';
    }
    if (e.name === 'SecurityError') {
      return 'This page’s address does not match the passkey’s. Check you are on the right domain.';
    }
    if (e.name === 'AbortError') return 'That was cancelled.';
  }
  return e instanceof Error ? e.message : String(e);
}
